import React, { useState, useEffect, useMemo } from 'react';
import {
  Megaphone,
  BellRing,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  Send,
  Users,
  GraduationCap,
  UserCheck,
  Check,
  Trash2,
  Eye,
  Search,
  ExternalLink,
  Clock,
  Smartphone,
  CheckCircle2,
  RefreshCw,
  X,
  Filter,
  Radio,
  Zap,
  Volume2
} from 'lucide-react';
import {
  subscribePopups,
  createPopup,
  togglePopupStatus,
  deletePopup
} from './popupService';
import {
  showSystemPushNotification,
  requestNotificationPermission
} from './pushNotificationService';

export default function PopupManagementSuperadminModule({
  allStudents = [],
  admins = [],
  user,
  db,
  isFirebaseActive
}) {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [deliveryChannel, setDeliveryChannel] = useState('both'); // 'both' | 'push' | 'popup'
  const [popupType, setPopupType] = useState('info'); // info, warning, urgent, success
  const [targetType, setTargetType] = useState('all'); // all, students, teachers, custom
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [buttonText, setButtonText] = useState('Tamam, Anladım');
  const [actionLink, setActionLink] = useState('');
  const [actionLinkText, setActionLinkText] = useState('Detayları Gör');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTestingPush, setIsTestingPush] = useState(false);

  // Özel kullanıcı seçici arama ve filtre
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all'); // all, students, teachers

  // Gönderilenler listesi filtresi
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive

  // Önizleme veya Kimler Gördü Modal State
  const [previewPopup, setPreviewPopup] = useState(null);
  const [viewDismissedPopup, setViewDismissedPopup] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Pop-up & Push listesini dinle
  useEffect(() => {
    setLoading(true);
    const unsub = subscribePopups(db, isFirebaseActive, (data) => {
      setPopups(data || []);
      setLoading(false);
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [db, isFirebaseActive]);

  // Aktif (arşivlenmemiş) öğrenciler ve öğretmenler havuzu
  const activeStudents = useMemo(() => {
    return (allStudents || []).filter(s => !s.isArchived);
  }, [allStudents]);

  const activeTeachers = useMemo(() => {
    return (admins || []).filter(a => a.role === 'admin' || a.role === 'superadmin');
  }, [admins]);

  // Birleştirilmiş kullanıcı listesi
  const combinedUserList = useMemo(() => {
    const list = [];
    activeTeachers.forEach(t => {
      list.push({
        id: String(t.id),
        name: t.name || t.username || 'Öğretmen',
        username: t.username || '',
        role: 'teacher',
        roleLabel: t.role === 'superadmin' ? 'Süper Admin' : 'Öğretmen / Koç',
        detail: t.role === 'superadmin' ? 'Yönetici' : 'Öğretmen'
      });
    });
    activeStudents.forEach(s => {
      list.push({
        id: String(s.id),
        name: s.name || s.username || 'Öğrenci',
        username: s.username || '',
        role: 'student',
        roleLabel: 'Öğrenci',
        detail: s.grade ? `${s.grade}. Sınıf` : 'Öğrenci'
      });
    });
    return list;
  }, [activeTeachers, activeStudents]);

  // Arama & filtreye göre filtrelenmiş kullanıcı listesi
  const filteredUsers = useMemo(() => {
    return combinedUserList.filter(u => {
      if (userRoleFilter === 'teachers' && u.role !== 'teacher') return false;
      if (userRoleFilter === 'students' && u.role !== 'student') return false;
      if (!userSearch.trim()) return true;
      const q = userSearch.toLowerCase();
      return (
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.username && u.username.toLowerCase().includes(q)) ||
        (u.detail && u.detail.toLowerCase().includes(q))
      );
    });
  }, [combinedUserList, userRoleFilter, userSearch]);

  // Bildirim Türü Konfigürasyonu
  const typeOptions = [
    {
      id: 'info',
      label: 'Bilgi & Duyuru',
      icon: Megaphone,
      color: 'text-sky-400',
      badgeBg: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
      borderActive: 'border-sky-500 ring-2 ring-sky-500/30 bg-sky-500/10'
    },
    {
      id: 'warning',
      label: 'Önemli Uyarı',
      icon: AlertTriangle,
      color: 'text-amber-400',
      badgeBg: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      borderActive: 'border-amber-500 ring-2 ring-amber-500/30 bg-amber-500/10'
    },
    {
      id: 'urgent',
      label: 'Acil Bildirim',
      icon: AlertCircle,
      color: 'text-rose-400',
      badgeBg: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
      borderActive: 'border-rose-500 ring-2 ring-rose-500/30 bg-rose-500/10'
    },
    {
      id: 'success',
      label: 'Kutlama & Tebrik',
      icon: Sparkles,
      color: 'text-emerald-400',
      badgeBg: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      borderActive: 'border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-500/10'
    }
  ];

  // Gönderim Kanalı Seçenekleri (Pop-up vs Push vs İkisi)
  const channelOptions = [
    {
      id: 'both',
      title: 'Her İkisi Birden (Önerilen)',
      description: 'Uygulama kapalıyken üstten sesli/titreşimli bildirim düşer; girildiğinde ise pop-up açılır.',
      icon: Zap,
      badge: 'Maksimum Etki',
      badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/40'
    },
    {
      id: 'push',
      title: 'Sadece Push Bildirim',
      description: 'Yalnızca telefonun üst bildirim çubuğundan anlık mesaj düşer, uygulama içi pop-up açılmaz.',
      icon: BellRing,
      badge: 'Cihaz Bildirimi',
      badgeColor: 'bg-sky-500/20 text-sky-400 border-sky-500/40'
    },
    {
      id: 'popup',
      title: 'Sadece Pop-up (Açılır Pencere)',
      description: 'Kullanıcı uygulamayı açtığında ekranın ortasında açılır pencere çıkar, üstten bildirim düşmez.',
      icon: Smartphone,
      badge: 'Uygulama İçi',
      badgeColor: 'bg-purple-500/20 text-purple-400 border-purple-500/40'
    }
  ];

  // Hedef Kitle Seçenekleri
  const targetOptions = [
    {
      id: 'all',
      title: 'Herkese',
      subtitle: `Tüm Öğrenciler (${activeStudents.length}) ve Öğretmenler (${activeTeachers.length})`,
      icon: Users,
      badge: `${activeStudents.length + activeTeachers.length} Kullanıcı`
    },
    {
      id: 'students',
      title: 'Tüm Öğrencilere',
      subtitle: 'Sadece aktif kayıtlı öğrencilere ulaşır',
      icon: GraduationCap,
      badge: `${activeStudents.length} Öğrenci`
    },
    {
      id: 'teachers',
      title: 'Tüm Öğretmenlere',
      subtitle: 'Sadece öğretmen ve koçlara ulaşır',
      icon: UserCheck,
      badge: `${activeTeachers.length} Öğretmen`
    },
    {
      id: 'custom',
      title: 'Bireysel / Özel Seçim',
      subtitle: 'Öğrenci ve/veya öğretmenleri tek tek elle seçin',
      icon: Filter,
      badge: `${selectedUserIds.length} Seçili`
    }
  ];

  // Kullanıcı seç/çıkar
  const toggleSelectUser = (id) => {
    setSelectedUserIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const selectAllFilteredUsers = () => {
    const ids = filteredUsers.map(u => u.id);
    setSelectedUserIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const clearSelectedUsers = () => {
    setSelectedUserIds([]);
  };

  // Kendi Cihazında Push Bildirim Testi
  const handleTestPushOnMyDevice = async () => {
    setIsTestingPush(true);
    try {
      const perm = await requestNotificationPermission();
      if (perm !== 'granted') {
        if (window.toast) window.toast.warning('Bildirim izni verilmedi. Lütfen tarayıcı/cihaz ayarlarından bildirimlere izin verin.');
        setIsTestingPush(false);
        return;
      }

      await showSystemPushNotification({
        title: title.trim() || 'Koçum Online Test Bildirimi',
        message: message.trim() || 'Bu bir test bildirimdir. Telefonunuzda/bilgisayarınızda üstten başarıyla göründü!',
        type: popupType,
        url: '/'
      });

      if (window.toast) window.toast.success('Test bildirimi cihazınıza gönderildi! Üst bildirim çubuğunu kontrol ediniz.');
    } catch (e) {
      console.error('Test push hatası:', e);
      if (window.toast) window.toast.error('Test bildirimi gönderilemedi.');
    } finally {
      setIsTestingPush(false);
    }
  };

  // Yeni Bildirim Gönderme (Push + Pop-up veya Seçilen Kanal)
  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      if (window.toast) window.toast.warning('Lütfen bir bildirim başlığı yazın!');
      return;
    }
    if (!message.trim()) {
      if (window.toast) window.toast.warning('Lütfen bir bildirim mesajı yazın!');
      return;
    }
    if (targetType === 'custom' && selectedUserIds.length === 0) {
      if (window.toast) window.toast.warning('Lütfen en az bir kullanıcı seçin!');
      return;
    }

    setIsSubmitting(true);

    try {
      // Seçili kullanıcıların isimlerini haritala
      const targetUserNames = targetType === 'custom'
        ? selectedUserIds.map(uid => {
            const found = combinedUserList.find(u => u.id === uid);
            return found ? found.name : uid;
          })
        : [];

      await createPopup({
        title: title.trim(),
        message: message.trim(),
        type: popupType,
        deliveryChannel, // 'both' | 'push' | 'popup'
        targetType,
        targetUserIds: targetType === 'custom' ? selectedUserIds : [],
        targetUserNames,
        buttonText: buttonText.trim() || 'Tamam, Anladım',
        actionLink: actionLink.trim(),
        actionLinkText: actionLinkText.trim(),
        authorName: user?.name || 'Süper Admin',
        db,
        isFirebaseActive
      });

      const channelName = deliveryChannel === 'both' ? 'Pop-up ve Push Bildirimi' : deliveryChannel === 'push' ? 'Push Bildirimi (Üstten)' : 'Pop-up Bildirimi';
      if (window.toast) {
        window.toast.success(`${channelName} başarıyla yayınlandı! Hedef kullanıcılara ulaştırılacaktır.`);
      }

      // Formu sıfırla
      setTitle('');
      setMessage('');
      setActionLink('');
      setSelectedUserIds([]);
      setTargetType('all');
    } catch (err) {
      console.error("Bildirim gönderme hatası:", err);
      if (window.toast) window.toast.error('Bildirim gönderilirken bir hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bildirim durumunu aç/kapat
  const handleToggleStatus = async (popup) => {
    const newStatus = !popup.isActive;
    try {
      setPopups(prev => prev.map(p => p.id === popup.id ? { ...p, isActive: newStatus } : p));
      await togglePopupStatus(popup.id, newStatus, db, isFirebaseActive);
      if (window.toast) {
        window.toast.success(newStatus ? 'Bildirim tekrar yayına alındı.' : 'Bildirim durduruldu (pasif yapıldı).');
      }
    } catch (err) {
      if (window.toast) window.toast.error('Durum güncellenemedi.');
    }
  };

  // Bildirim sil
  const handleDeletePopup = async (popupId) => {
    try {
      setPopups(prev => prev.filter(p => p.id !== popupId));
      await deletePopup(popupId, db, isFirebaseActive);
      setDeleteConfirmId(null);
      if (window.toast) window.toast.success('Bildirim kalıcı olarak silindi.');
    } catch (err) {
      if (window.toast) window.toast.error('Bildirim silinemedi.');
    }
  };

  // Filtrelenmiş pop-up listesi
  const filteredPopups = useMemo(() => {
    return popups.filter(p => {
      if (statusFilter === 'active') return !!p.isActive;
      if (statusFilter === 'inactive') return !p.isActive;
      return true;
    });
  }, [popups, statusFilter]);

  // İstatistikler
  const stats = useMemo(() => {
    const total = popups.length;
    const active = popups.filter(p => p.isActive).length;
    const totalDismissed = popups.reduce((acc, p) => acc + (Array.isArray(p.dismissedBy) ? p.dismissedBy.length : 0), 0);
    return { total, active, totalDismissed };
  }, [popups]);

  // Seçili türe göre önizleme renkleri
  const selectedTypeConfig = typeOptions.find(t => t.id === popupType) || typeOptions[0];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-8 custom-scrollbar">
      
      {/* 1. ÜST BAŞLIK VE İSTATİSTİKLER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-tr from-orange-600 to-amber-500 rounded-2xl shadow-lg shadow-orange-500/20 text-white">
              <Megaphone size={26} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  Pop-up & Push Bildirim Merkezi
                </h1>
                <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-orange-500/20 border border-orange-500/40 text-orange-400">
                  Superadmin
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Uygulama kapalıyken <b>üstten düşen Push Bildirimleri</b> ve uygulama içi <b>Pop-up pencerelerini</b> tek panelden yönetin.
              </p>
            </div>
          </div>
        </div>

        {/* Sayaç Kartları & Test Butonu */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={handleTestPushOnMyDevice}
            disabled={isTestingPush}
            className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 text-xs font-bold transition-all shadow-lg active:scale-95 disabled:opacity-50"
            title="Şu anki cihazınıza anlık bir test push bildirimi gönderir"
          >
            {isTestingPush ? <RefreshCw size={14} className="animate-spin" /> : <BellRing size={14} />}
            <span>Cihazımda Test Et</span>
          </button>

          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-2.5 px-3 text-center">
            <div className="text-[10px] text-slate-400 font-medium">Toplam</div>
            <div className="text-lg font-black text-white">{stats.total}</div>
          </div>
          <div className="bg-slate-900/80 border border-emerald-500/30 rounded-2xl p-2.5 px-3 text-center">
            <div className="text-[10px] text-emerald-400 font-medium">Aktif</div>
            <div className="text-lg font-black text-emerald-400">{stats.active}</div>
          </div>
          <div className="bg-slate-900/80 border border-sky-500/30 rounded-2xl p-2.5 px-3 text-center">
            <div className="text-[10px] text-sky-400 font-medium">Okunma</div>
            <div className="text-lg font-black text-sky-400">{stats.totalDismissed}</div>
          </div>
        </div>
      </div>

      {/* 2. OLUŞTURMA FORMU & CANLI AKILLI TELEFON ÖNİZLEMESİ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* SOL: OLUŞTURMA FORMU (7 Kolon) */}
        <div className="lg:col-span-7 bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-7 shadow-xl">
          <div className="flex items-center gap-2 mb-6">
            <Send size={18} className="text-orange-400" />
            <h2 className="text-lg font-bold text-white">Yeni Bildirim Oluştur ve Yayınla</h2>
          </div>

          <form onSubmit={handleSendNotification} className="space-y-6">
            
            {/* A. GÖNDERİM KANALI SEÇİMİ (POP-UP / PUSH / HER İKİSİ) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                  <Radio size={14} />
                  <span>1. Gönderim Kanalı (Nasıl İletilsin?)</span>
                </label>
                <span className="text-[11px] text-slate-400">İsteğe göre sadece biri veya ikisi birden</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {channelOptions.map(ch => {
                  const Icon = ch.icon;
                  const isSelected = deliveryChannel === ch.id;
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => setDeliveryChannel(ch.id)}
                      className={`text-left p-3.5 rounded-2xl border transition-all flex flex-col justify-between ${
                        isSelected
                          ? 'border-orange-500 bg-orange-500/15 ring-2 ring-orange-500/30 text-white'
                          : 'border-slate-800 bg-slate-800/40 hover:bg-slate-800/80 text-slate-400'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1 w-full mb-2">
                        <div className={`p-2 rounded-xl shrink-0 ${isSelected ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                          <Icon size={16} />
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${ch.badgeColor}`}>
                          {ch.badge}
                        </span>
                      </div>
                      <div>
                        <div className={`font-bold text-xs ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {ch.title}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1 leading-snug line-clamp-2">
                          {ch.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* B. HEDEF KİTLE SEÇİMİ */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                2. Hedef Kitleyi Belirleyin
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {targetOptions.map(t => {
                  const Icon = t.icon;
                  const isSelected = targetType === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTargetType(t.id)}
                      className={`text-left p-3.5 rounded-2xl border transition-all flex items-start gap-3 ${
                        isSelected
                          ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500/30 text-white'
                          : 'border-slate-800 bg-slate-800/40 hover:bg-slate-800/80 text-slate-300'
                      }`}
                    >
                      <div className={`p-2 rounded-xl shrink-0 ${isSelected ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm truncate">{t.title}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-md font-semibold bg-slate-800 text-slate-300 border border-slate-700">
                            {t.badge}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{t.subtitle}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* C. ÖZEL KULLANICI SEÇİCİ (Yalnızca "custom" seçildiğinde görünür) */}
            {targetType === 'custom' && (
              <div className="p-4 bg-slate-950/70 border border-orange-500/30 rounded-2xl space-y-3 animate-fade-in">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Filter size={15} className="text-orange-400" />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">
                      Gönderilecek Kişileri Seçin ({selectedUserIds.length} Seçili)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllFilteredUsers}
                      className="text-xs text-orange-400 hover:text-orange-300 font-semibold px-2 py-1 rounded bg-orange-500/10 hover:bg-orange-500/20"
                    >
                      Listelenenleri Seç
                    </button>
                    <button
                      type="button"
                      onClick={clearSelectedUsers}
                      className="text-xs text-slate-400 hover:text-slate-300 font-semibold px-2 py-1 rounded bg-slate-800 hover:bg-slate-700"
                    >
                      Temizle
                    </button>
                  </div>
                </div>

                {/* Arama ve Rol Filtresi */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="İsim veya kullanıcı adı ile ara..."
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                    />
                    {userSearch && (
                      <button
                        type="button"
                        onClick={() => setUserSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800">
                    <button
                      type="button"
                      onClick={() => setUserRoleFilter('all')}
                      className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-colors ${userRoleFilter === 'all' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      Tümü ({combinedUserList.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserRoleFilter('teachers')}
                      className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-colors ${userRoleFilter === 'teachers' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      Öğretmenler ({activeTeachers.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserRoleFilter('students')}
                      className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-colors ${userRoleFilter === 'students' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      Öğrenciler ({activeStudents.length})
                    </button>
                  </div>
                </div>

                {/* Kullanıcı Listesi */}
                <div className="max-h-56 overflow-y-auto space-y-1.5 custom-scrollbar pr-1">
                  {filteredUsers.length === 0 ? (
                    <div className="text-center py-6 text-xs text-slate-500">
                      Arama kriterine uygun kullanıcı bulunamadı.
                    </div>
                  ) : (
                    filteredUsers.map(u => {
                      const isSelected = selectedUserIds.includes(u.id);
                      return (
                        <div
                          key={u.id}
                          onClick={() => toggleSelectUser(u.id)}
                          className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer border transition-all ${
                            isSelected
                              ? 'bg-orange-500/15 border-orange-500/40 text-white'
                              : 'bg-slate-900/60 border-slate-800/80 hover:bg-slate-800/60 text-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all ${
                              isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-600 bg-slate-800'
                            }`}>
                              {isSelected && <Check size={12} strokeWidth={3} />}
                            </div>
                            <div className="truncate">
                              <span className="font-semibold text-xs text-white truncate block">{u.name}</span>
                              <span className="text-[10px] text-slate-400">@{u.username} • {u.detail}</span>
                            </div>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold shrink-0 ${
                            u.role === 'teacher' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          }`}>
                            {u.roleLabel}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* D. BİLDİRİM TÜRÜ */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                3. Bildirim Türü & Görsel Tema
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {typeOptions.map(opt => {
                  const Icon = opt.icon;
                  const isSelected = popupType === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPopupType(opt.id)}
                      className={`p-3 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all ${
                        isSelected
                          ? opt.borderActive
                          : 'border-slate-800 bg-slate-800/30 hover:bg-slate-800/70 text-slate-400'
                      }`}
                    >
                      <Icon size={20} className={opt.color} />
                      <span className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-slate-400'}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* E. BAŞLIK & İÇERİK */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                  4. Bildirim Başlığı <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Örn: Haftalık Koçluk Görüşmeleri ve Deneme Takvimi"
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
                  maxLength={100}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                    5. Bildirim Mesajı <span className="text-orange-500">*</span>
                  </label>
                  <span className="text-[11px] text-slate-500">{message.length}/500 Karakter</span>
                </div>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Kullanıcıların telefon ekranında ve açılan pencerede okuyacağı mesajı yazın..."
                  className="w-full p-4 bg-slate-950 border border-slate-800 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors custom-scrollbar leading-relaxed"
                  maxLength={500}
                />
              </div>
            </div>

            {/* F. EKSTRA AYARLAR (Buton Metni & Opsiyonel Link) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-800/80">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Kapat Butonu Metni
                </label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={e => setButtonText(e.target.value)}
                  placeholder="Örn: Tamam, Anladım"
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Opsiyonel Link (URL)
                </label>
                <input
                  type="url"
                  value={actionLink}
                  onChange={e => setActionLink(e.target.value)}
                  placeholder="https://... (boş bırakılabilir)"
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Link Butonu Metni
                </label>
                <input
                  type="text"
                  value={actionLinkText}
                  onChange={e => setActionLinkText(e.target.value)}
                  placeholder="Örn: Detayları Gör"
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>

            {/* GÖNDER BUTONU */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white font-bold text-sm sm:text-base tracking-wide shadow-xl shadow-orange-500/25 transition-all flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>Gönderiliyor...</span>
                </>
              ) : (
                <>
                  <Send size={18} />
                  <span>
                    {deliveryChannel === 'both' ? 'Push Bildirim ve Pop-up Olarak Yayınla' : deliveryChannel === 'push' ? 'Üstten Push Bildirim Olarak Gönder' : 'Uygulama İçi Pop-up Olarak Yayınla'}
                  </span>
                </>
              )}
            </button>

          </form>
        </div>

        {/* SAĞ: CANLI AKILLI TELEFON SİMÜLASYONU (5 Kolon) */}
        <div className="lg:col-span-5 flex flex-col items-center">
          <div className="w-full flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
              <Smartphone size={16} className="text-orange-400" />
              <span>Canlı Akıllı Telefon Önizlemesi</span>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-800 text-slate-400 border border-slate-700">
              {deliveryChannel === 'both' ? 'Push + Pop-up' : deliveryChannel === 'push' ? 'Sadece Push' : 'Sadece Pop-up'}
            </span>
          </div>

          {/* Akıllı Telefon Mockup */}
          <div className="relative w-full max-w-[340px] h-[640px] bg-slate-950 rounded-[44px] border-[8px] border-slate-800 shadow-2xl p-4 flex flex-col overflow-hidden ring-1 ring-slate-700">
            
            {/* Telefon Üst Ahize / Ada & Saat Barı */}
            <div className="w-full flex items-center justify-between px-3 text-[10px] text-slate-400 font-semibold mb-2 shrink-0">
              <span>09:41</span>
              <div className="w-20 h-4 bg-slate-800 rounded-full shrink-0" />
              <div className="flex items-center gap-1">
                <span>5G</span>
                <span>%98</span>
              </div>
            </div>

            {/* 1. ÜSTTEN DÜŞEN PUSH BİLDİRİM BANNER'I (Eğer 'push' veya 'both' seçiliyse) */}
            {(deliveryChannel === 'both' || deliveryChannel === 'push') && (
              <div className="w-full bg-slate-900/95 backdrop-blur-md border border-slate-700 rounded-2xl p-3 shadow-2xl mb-3 animate-fade-in ring-1 ring-white/10 shrink-0">
                <div className="flex items-center justify-between gap-1 mb-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-md bg-orange-600 flex items-center justify-center text-white text-[9px] font-black">
                      K
                    </div>
                    <span className="text-[11px] font-bold text-slate-200">Koçum Online</span>
                    <span className="text-[9px] text-slate-500">• Şimdi</span>
                  </div>
                  <Volume2 size={12} className="text-slate-400" />
                </div>
                <div className="text-xs font-bold text-white truncate">
                  {title.trim() || 'Yeni Bildirim Başlığı'}
                </div>
                <div className="text-[11px] text-slate-300 line-clamp-2 mt-0.5 leading-snug">
                  {message.trim() || 'Telefon kapalıyken veya kilit ekranındayken üstten ses ve titreşimle böyle görünecektir.'}
                </div>
              </div>
            )}

            {/* Arka Planda Sahte Uygulama Arayüzü */}
            <div className="absolute inset-x-4 top-24 bottom-6 rounded-2xl bg-slate-900/30 p-4 opacity-25 pointer-events-none select-none flex flex-col gap-3">
              <div className="h-4 w-28 bg-slate-700 rounded-md" />
              <div className="h-16 w-full bg-slate-800 rounded-xl" />
              <div className="h-20 w-full bg-slate-800 rounded-xl" />
              <div className="h-20 w-full bg-slate-800 rounded-xl" />
            </div>

            {/* 2. UYGULAMA İÇİ POP-UP KARTI (Eğer 'popup' veya 'both' seçiliyse) */}
            {(deliveryChannel === 'both' || deliveryChannel === 'popup') ? (
              <div className="relative z-10 m-auto w-full bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-4 flex flex-col overflow-hidden animate-fade-in">
                
                {/* Pop-up Rozet & İkon */}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`p-2 rounded-xl shrink-0 ${selectedTypeConfig.badgeBg}`}>
                    <selectedTypeConfig.icon size={16} />
                  </div>
                  <div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${selectedTypeConfig.badgeBg}`}>
                      {selectedTypeConfig.label}
                    </span>
                  </div>
                </div>

                {/* Başlık */}
                <h3 className="font-bold text-sm text-white mb-2 leading-tight">
                  {title.trim() || 'Bildirim Başlığı Buraya Gelecek'}
                </h3>

                {/* Mesaj */}
                <div className="text-xs text-slate-300 leading-relaxed max-h-32 overflow-y-auto mb-3 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800">
                  {message.trim() || 'Uygulama açıldığında ekranda beliren tam içerik metni burada görüntülenecektir.'}
                </div>

                {/* Gönderen İmzası */}
                <div className="text-[10px] text-slate-500 mb-3">
                  Gönderen: <b className="text-slate-400">{user?.name || 'Sistem Yöneticisi'}</b>
                </div>

                {/* Butonlar */}
                <div className="space-y-1.5">
                  {actionLink.trim() && (
                    <div className="w-full py-1.5 px-3 rounded-lg border border-slate-700 bg-slate-800 text-[11px] font-semibold text-slate-300 text-center flex items-center justify-center gap-1">
                      <ExternalLink size={11} />
                      <span>{actionLinkText || 'Detayları Gör'}</span>
                    </div>
                  )}
                  <div className={`w-full py-2 px-3 rounded-xl text-center text-xs font-bold text-white shadow-md ${
                    popupType === 'urgent' ? 'bg-rose-600' :
                    popupType === 'warning' ? 'bg-amber-600' :
                    popupType === 'success' ? 'bg-emerald-600' : 'bg-sky-600'
                  }`}>
                    {buttonText.trim() || 'Tamam, Anladım'}
                  </div>
                </div>

              </div>
            ) : (
              <div className="m-auto text-center p-6 text-slate-500 text-xs">
                <BellRing size={32} className="mx-auto mb-2 text-sky-400 opacity-60" />
                <p className="font-semibold text-slate-400">Sadece Push Bildirim Seçili</p>
                <p className="text-[11px] mt-1 text-slate-500">Uygulama içi açılır pencere gösterilmeyecek, sadece telefonun üst durum çubuğundan sesli bildirim düşecektir.</p>
              </div>
            )}

            {/* Telefon Alt Çizgisi */}
            <div className="w-28 h-1 bg-slate-700 rounded-full mx-auto mt-auto shrink-0" />
          </div>
        </div>

      </div>

      {/* 3. YAYINLANAN BİLDİRİMLER LİSTESİ */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-7 shadow-xl space-y-6">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-orange-400" />
            <h2 className="text-lg font-bold text-white">Yayınlanan Bildirimler & Gönderim Geçmişi</h2>
            <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-slate-800 text-slate-400 border border-slate-700">
              {filteredPopups.length} Adet
            </span>
          </div>

          {/* Filtre Butonları */}
          <div className="flex gap-1.5 bg-slate-950 p-1 rounded-xl border border-slate-800 self-start sm:self-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${statusFilter === 'all' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Tümü ({popups.length})
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${statusFilter === 'active' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Aktif Yayındakiler ({popups.filter(p => p.isActive).length})
            </button>
            <button
              onClick={() => setStatusFilter('inactive')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${statusFilter === 'inactive' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Durdurulanlar ({popups.filter(p => !p.isActive).length})
            </button>
          </div>
        </div>

        {/* Tablo / Kart Listesi */}
        {loading ? (
          <div className="text-center py-12 text-slate-500 flex items-center justify-center gap-2">
            <RefreshCw size={18} className="animate-spin text-orange-400" />
            <span>Bildirimler yükleniyor...</span>
          </div>
        ) : filteredPopups.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-slate-800 rounded-2xl text-slate-500">
            Henüz bu filtreye uygun bildirim bulunmuyor.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filteredPopups.map(p => {
              const typeCfg = typeOptions.find(t => t.id === p.type) || typeOptions[0];
              const Icon = typeCfg.icon;
              const dismissedCount = Array.isArray(p.dismissedBy) ? p.dismissedBy.length : 0;
              const channel = p.deliveryChannel || 'both';

              // Hedef metni
              let targetLabel = 'Herkese';
              if (p.targetType === 'students') targetLabel = 'Tüm Öğrenciler';
              if (p.targetType === 'teachers') targetLabel = 'Tüm Öğretmenler';
              if (p.targetType === 'custom') {
                targetLabel = `Özel (${(p.targetUserIds || []).length} Kişi)`;
              }

              // Tarih
              let dateStr = '';
              if (p.createdAt) {
                try {
                  dateStr = new Date(p.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                } catch (e) {}
              }

              return (
                <div
                  key={p.id}
                  className={`p-4 sm:p-5 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                    p.isActive
                      ? 'bg-slate-950/70 border-slate-800/90 hover:border-slate-700'
                      : 'bg-slate-950/30 border-slate-900 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3.5 min-w-0">
                    <div className={`p-3 rounded-2xl shrink-0 mt-0.5 ${typeCfg.badgeBg}`}>
                      <Icon size={20} />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-base text-white truncate max-w-md">
                          {p.title}
                        </span>

                        {/* Kanal Rozeti */}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border flex items-center gap-1 ${
                          channel === 'both' ? 'bg-orange-500/20 text-orange-400 border-orange-500/40' :
                          channel === 'push' ? 'bg-sky-500/20 text-sky-400 border-sky-500/40' :
                          'bg-purple-500/20 text-purple-400 border-purple-500/40'
                        }`}>
                          {channel === 'both' ? <Zap size={10} /> : channel === 'push' ? <BellRing size={10} /> : <Smartphone size={10} />}
                          <span>{channel === 'both' ? 'Push + Pop-up' : channel === 'push' ? 'Sadece Push' : 'Sadece Pop-up'}</span>
                        </span>

                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${typeCfg.badgeBg}`}>
                          {typeCfg.label}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-800 text-slate-300 border border-slate-700 flex items-center gap-1">
                          <Users size={10} />
                          {targetLabel}
                        </span>
                        {p.isActive ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            Yayında
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-800 text-slate-400 border border-slate-700">
                            Durduruldu
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                        {p.message}
                      </p>

                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 pt-1">
                        <span>{dateStr}</span>
                        <span>•</span>
                        <span>Gönderen: <b className="text-slate-400">{p.authorName || 'Yönetici'}</b></span>
                      </div>
                    </div>
                  </div>

                  {/* Sağ Aksiyonlar */}
                  <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                    
                    {/* Okunma / Kapatılma Butonu */}
                    <button
                      onClick={() => setViewDismissedPopup(p)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-xs font-semibold text-slate-300 transition-colors"
                      title="Kimlerin görüp kapattığını incele"
                    >
                      <Eye size={14} className="text-sky-400" />
                      <span>{dismissedCount} Gördü</span>
                    </button>

                    {/* Önizleme */}
                    <button
                      onClick={() => setPreviewPopup(p)}
                      className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-400 hover:text-white transition-colors"
                      title="Önizle"
                    >
                      <Smartphone size={16} />
                    </button>

                    {/* Durdur / Aktif Et */}
                    <button
                      onClick={() => handleToggleStatus(p)}
                      className={`px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                        p.isActive
                          ? 'border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                          : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                      }`}
                      title={p.isActive ? 'Bildirim yayınını geçici olarak durdur' : 'Bildirim yayınını tekrar başlat'}
                    >
                      {p.isActive ? 'Durdur' : 'Yayına Al'}
                    </button>

                    {/* Sil Butonu */}
                    <button
                      onClick={() => setDeleteConfirmId(p.id)}
                      className="p-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 transition-colors"
                      title="Kalıcı Olarak Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* SİLME ONAY MODALI */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl text-center space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center mx-auto">
              <Trash2 size={24} />
            </div>
            <div>
              <h3 className="font-bold text-lg text-white">Bildirim Silinsin mi?</h3>
              <p className="text-xs text-slate-400 mt-1">
                Bu bildirim kalıcı olarak silinecektir.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-800 bg-slate-800/80 hover:bg-slate-800 text-xs font-bold text-slate-300"
              >
                Vazgeç
              </button>
              <button
                onClick={() => handleDeletePopup(deleteConfirmId)}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-xs font-bold text-white shadow-lg shadow-rose-900/30"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KİMLER GÖRDÜ DETAY MODALI */}
      {viewDismissedPopup && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-emerald-400" />
                <h3 className="font-bold text-sm text-white">Görüntüleme & Onay Listesi</h3>
              </div>
              <button
                onClick={() => setViewDismissedPopup(null)}
                className="p-1.5 rounded-xl bg-slate-800 text-slate-400 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="py-3">
              <div className="text-xs font-bold text-white truncate">{viewDismissedPopup.title}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Toplam <b>{(viewDismissedPopup.dismissedBy || []).length}</b> kullanıcı bu bildirimi gördü.
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-1.5 custom-scrollbar pr-1 my-2">
              {(viewDismissedPopup.dismissedBy || []).length === 0 ? (
                <div className="text-center py-8 text-xs text-slate-500">
                  Henüz kimse bu bildirimi görüp onaylamadı.
                </div>
              ) : (
                (viewDismissedPopup.dismissedBy || []).map((uid, idx) => {
                  const userObj = combinedUserList.find(u => u.id === String(uid));
                  return (
                    <div
                      key={idx}
                      className="p-2.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-[11px]">
                          {userObj?.name ? userObj.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="font-semibold text-white">{userObj?.name || `Kullanıcı ID: ${uid}`}</div>
                          <div className="text-[10px] text-slate-400">{userObj?.roleLabel || 'Kullanıcı'}</div>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1">
                        <Check size={12} /> Okundu
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <button
              onClick={() => setViewDismissedPopup(null)}
              className="w-full mt-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white"
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* DETAYLI ÖNİZLEME MODALI */}
      {previewPopup && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-3xl p-6 shadow-2xl space-y-4">
            <button
              onClick={() => setPreviewPopup(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white"
            >
              <X size={16} />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-orange-500/20 text-orange-400 border border-orange-500/40">
                Bildirim Önizlemesi
              </span>
            </div>
            <h3 className="text-xl font-black text-white">{previewPopup.title}</h3>
            <div className="text-sm text-slate-200 leading-relaxed bg-slate-950 p-4 rounded-2xl border border-slate-800 whitespace-pre-wrap">
              {previewPopup.message}
            </div>
            <div className="flex gap-2 pt-2">
              {previewPopup.actionLink && (
                <button
                  onClick={() => window.open(previewPopup.actionLink, '_blank')}
                  className="flex-1 py-3 rounded-xl border border-slate-700 bg-slate-800 text-xs font-bold text-white flex items-center justify-center gap-1.5"
                >
                  <ExternalLink size={14} />
                  <span>{previewPopup.actionLinkText || 'Bağlantıyı Aç'}</span>
                </button>
              )}
              <button
                onClick={() => setPreviewPopup(null)}
                className="flex-1 py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-xs font-bold text-white shadow-lg shadow-orange-900/30"
              >
                {previewPopup.buttonText || 'Tamam, Anladım'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
